import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
} from 'react-router-dom';
import { AuthProvider } from './hooks/useAuth';
import { ToastProvider } from './hooks/useToast';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

// Auth pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import Verify from './pages/Verify';
import ForgotPassword from './pages/ForgotPassword';

// Dashboard
import Dashboard from './pages/Dashboard';
import CreateSite from './pages/CreateSite';

// Site pages
import SiteOverview from './pages/SiteOverview';
import ContentOverview from './pages/ContentOverview';
import SiteEditor from './pages/SiteEditor';
import HeroEditor from './pages/HeroEditor';
import AboutEditor from './pages/AboutEditor';
import ProductList from './pages/ProductList';
import ProductEditor from './pages/ProductEditor';
import GalleryEditor from './pages/GalleryEditor';
import ContactEditor from './pages/ContactEditor';
import MediaLibrary from './pages/MediaLibrary';
import PublishPage from './pages/PublishPage';
import Analytics from './pages/Analytics';
import DomainSettings from './pages/DomainSettings';
import Onboarding from './pages/Onboarding';
import VersionHistory from './pages/VersionHistory';
import PostList from './pages/PostList';
import PostEditor from './pages/PostEditor';
import CategoryList from './pages/CategoryList';
import CategoryEditor from './pages/CategoryEditor';
import MenuList from './pages/MenuList';
import MenuEditor from './pages/MenuEditor';
import TestimonialList from './pages/TestimonialList';
import TestimonialEditor from './pages/TestimonialEditor';
import HoursList from './pages/HoursList';
import HoursEditor from './pages/HoursEditor';
import CtaEditor from './pages/CtaEditor';
import BrandingEditor from './pages/BrandingEditor';
import SectionsEditor from './pages/SectionsEditor';
import AppearancePage from './pages/AppearancePage';
import SeoPage from './pages/SeoPage';
import MessageList from './pages/MessageList';
import Billing from './pages/Billing';

import './styles/global.css';

/** Redirect helper — preserves bookmark compatibility */
function DeployRedirect() {
  return <Navigate to="../publish" replace />;
}

// Data router (createBrowserRouter) rather than <BrowserRouter> so editors can
// block in-app navigation via useBlocker when there are unsaved changes.
const router = createBrowserRouter(
  createRoutesFromElements(
    <>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* Protected standalone routes (no sidebar) */}
            <Route
              path="/sites/:siteId/setup"
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            {/* Protected routes with layout */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/sites/new" element={<CreateSite />} />

              {/* Site routes */}
              <Route path="/sites/:siteId" element={<SiteOverview />} />
              <Route path="/sites/:siteId/content" element={<ContentOverview />} />
              <Route path="/sites/:siteId/content/site" element={<SiteEditor />} />
              <Route path="/sites/:siteId/content/branding" element={<BrandingEditor />} />
              <Route path="/sites/:siteId/content/sections" element={<SectionsEditor />} />
              <Route path="/sites/:siteId/content/hero" element={<HeroEditor />} />
              <Route path="/sites/:siteId/content/cta" element={<CtaEditor />} />
              <Route path="/sites/:siteId/content/about" element={<AboutEditor />} />
              <Route path="/sites/:siteId/content/products" element={<ProductList />} />
              <Route path="/sites/:siteId/content/products/:id" element={<ProductEditor />} />
              <Route path="/sites/:siteId/content/categories" element={<CategoryList />} />
              <Route path="/sites/:siteId/content/categories/:id" element={<CategoryEditor />} />
              <Route path="/sites/:siteId/content/gallery" element={<GalleryEditor />} />
              <Route path="/sites/:siteId/content/menu" element={<MenuList />} />
              <Route path="/sites/:siteId/content/menu/:id" element={<MenuEditor />} />
              <Route path="/sites/:siteId/content/testimonials" element={<TestimonialList />} />
              <Route path="/sites/:siteId/content/testimonials/:id" element={<TestimonialEditor />} />
              <Route path="/sites/:siteId/content/hours" element={<HoursList />} />
              <Route path="/sites/:siteId/content/hours/:id" element={<HoursEditor />} />
              <Route path="/sites/:siteId/content/blog" element={<PostList />} />
              <Route path="/sites/:siteId/content/blog/:id" element={<PostEditor />} />
              <Route path="/sites/:siteId/content/contact" element={<ContactEditor />} />
              <Route path="/sites/:siteId/media" element={<MediaLibrary />} />
              <Route path="/sites/:siteId/publish" element={<PublishPage />} />
              <Route path="/sites/:siteId/deploy" element={<DeployRedirect />} />
              <Route path="/sites/:siteId/versions" element={<VersionHistory />} />
              <Route path="/sites/:siteId/domain" element={<DomainSettings />} />
              <Route path="/sites/:siteId/appearance" element={<AppearancePage />} />
              <Route path="/sites/:siteId/seo" element={<SeoPage />} />
              <Route path="/sites/:siteId/analytics" element={<Analytics />} />
              <Route path="/sites/:siteId/messages" element={<MessageList />} />
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </>,
  ),
);

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
